/**
 * useDraftDocuments Hook
 *
 * Fetches draft and AUSTRAC documents from the REST API.
 * Extracted from ReviewSign and AustracDocuments.
 *
 * Responsibilities:
 * - Fetch all draft documents on mount
 * - Filter by folder/prefix (e.g. "drafts/austrac/{sector}/")
 * - Upload AUSTRAC docs on continue
 */

import { useState, useEffect, useCallback } from 'react';
import {
  getDraftDocuments,
  getAustracDocuments,
  uploadSignedDocument,
  S3Document,
} from '../services/agreementService';

export type { S3Document };

/** Map sector to AUSTRAC folder */
export const SECTOR_FOLDER_MAP: Record<string, string> = {
  bullion_dealer: 'precious-metals',
};

export interface UseDraftDocumentsOptions {
  /** If true, fetch only AUSTRAC documents */
  austracOnly?: boolean;
  /** Sector value for filtering AUSTRAC docs by folder */
  sector?: string;
}

export interface UseDraftDocumentsReturn {
  /** All fetched documents */
  allDocuments: S3Document[];
  /** Filtered documents (by sector if austracOnly) */
  documents: S3Document[];
  /** Whether documents are loading */
  isLoading: boolean;
  /** Error message (if fetch failed) */
  error: string;
  /** Re-fetch documents */
  refresh: () => Promise<void>;
  /** Upload AUSTRAC docs to S3 (for AustracDocuments continue) */
  uploadAustracDocs: (docs: S3Document[]) => Promise<void>;
}

export function useDraftDocuments(options: UseDraftDocumentsOptions = {}): UseDraftDocumentsReturn {
  const { austracOnly = false, sector = '' } = options;

  const [allDocuments, setAllDocuments] = useState<S3Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDocs = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const docs = austracOnly ? await getAustracDocuments() : await getDraftDocuments();
      setAllDocuments(docs);
    } catch (err) {
      setError('Failed to load documents');
    } finally {
      setIsLoading(false);
    }
  }, [austracOnly]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  // Filter by sector folder for AUSTRAC — keys may come from either
  // the dedicated austrac/ prefix or the legacy drafts/austrac/ prefix.
  const folder = SECTOR_FOLDER_MAP[sector] ?? sector;
  const documents = austracOnly && folder
    ? allDocuments.filter((d) =>
        (d.key.startsWith(`austrac/${folder}/`) || d.key.startsWith(`drafts/austrac/${folder}/`)) &&
        !d.key.endsWith('/')
      )
    : allDocuments.filter((d) => !d.key.endsWith('/'));

  const uploadAustracDocs = useCallback(async (docs: S3Document[]) => {
    const sessionToken = localStorage.getItem('reg_session_token') || '';
    if (!sessionToken || docs.length === 0) return;

    for (const doc of docs) {
      try {
        const res = await fetch(doc.downloadUrl);
        if (!res.ok) throw new Error(`Failed to fetch ${doc.fileName}: ${res.status}`);
        const bytes = new Uint8Array(await res.arrayBuffer());
        await uploadSignedDocument(`austrac_${doc.fileName}`, bytes, sessionToken);
      } catch {
        // Non-blocking — continue to next doc
      }
    }
  }, []);

  return {
    allDocuments,
    documents,
    isLoading,
    error,
    refresh: fetchDocs,
    uploadAustracDocs,
  };
}
